// src/encounter/invoice-add-service-dialog.tsx
// Add a billable service to the encounter invoice — the Wrap-Up invoice section's "+" action, and
// the Pending orders "Choose a service" path for a NoPrice order.
//
// SAME SCREEN AS PM (Andrew, 8 Oct): pm-react's Edit appointment → Billing → "+ Add service"
// (`src/billing/components/service-picker-dialog.tsx`). Same endpoint (`useServiceFees` → POST
// /Billing/Services/Fees, scoped by provider + location + patient), the same Provider Fees / All
// toggle, the list shown on open with each row's code and fee, already-added rows marked "Added",
// one click adds and closes, and the same error / no-patient / empty states. Recomposed on DS
// (Modal, Segmented, TextField, Skeleton, Button, Icon) — PM uses shadcn here.
//
// Kept from Flutter (`AddProcedureBillingCodeWidget`, wrap_up_billing_screen.dart:612-659), because
// it is a billing rule rather than chrome: NO DIAGNOSIS → refuse with "Please add diagnosis
// first." (:613-619). The duplicate rule (:633-644) is now the disabled "Added" row.
import { useState } from 'react';
import { Banner, Button, Modal, Segmented, Skeleton, TextField } from '@scripsteam/scrips-signal-ds';
import { Search } from 'lucide-react';
import {
  canFetchServiceFees,
  useServiceFees,
  type ServiceFee,
  type ServiceFeesType,
} from '@/billing/services/use-service-fees';
import { formatMoney } from '@/shared/lib/money';
import { isEmptyGuid } from '@/shared/lib/empty-guid';

export interface InvoiceAddServiceDialogProps {
  open: boolean;
  onClose: () => void;
  /** Service-MASTER ids already on the invoice — shown as "Added" and not selectable. */
  existingServiceIds: string[];
  /** True when the encounter has ≥1 diagnosis. False → the dialog refuses (Flutter :613). */
  hasDiagnosis: boolean;
  /** Add the chosen service (host wires `useAddRemoveService` → `addedServices`). */
  onAdd: (serviceId: string) => void;
  /** An add is in flight: the rows are disabled. */
  busy?: boolean;
  /** The invoice's provider, location and patient — the scope of the fee list. */
  providerId?: string;
  locationId?: string;
  patientId?: string;
  currency?: string;
  /**
   * The invoice (the source of provider / location / patient) is still loading. Shows the
   * loading rows and holds the request, so the dialog never flashes "no provider" or "no patient
   * yet" and never forces All on an invoice that has a provider.
   */
  scopeLoading?: boolean;
  /**
   * PROD-2594: the Pending orders "Choose service" path seeds the search with the order's code.
   * Read once on mount; the host remounts the dialog per order (`key`).
   */
  initialQuery?: string;
  /** Dialog title; defaults to "Add service". */
  title?: string;
}

/** The fee at the invoice's location, else the first fee (pm-react `feeFor`). */
function feeFor(locationId: string | undefined, fees: ServiceFee[] | undefined): number | undefined {
  if (!fees || fees.length === 0) return undefined;
  const match = locationId ? fees.find((f) => f.location?.id === locationId) : undefined;
  return (match ?? fees[0])?.fee;
}

/** HTTP status of a failed request, duck-typed (pm-react `httpStatusOf`). */
function httpStatusOf(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const status = (error as { status?: unknown }).status;
  return typeof status === 'number' ? status : undefined;
}

export function InvoiceAddServiceDialog({
  open,
  onClose,
  existingServiceIds,
  hasDiagnosis,
  onAdd,
  busy,
  providerId,
  locationId,
  patientId,
  currency = 'AED',
  scopeLoading = false,
  initialQuery,
  title = 'Add service',
}: InvoiceAddServiceDialogProps) {
  // Server-side search on every keystroke, no debounce — as in PM (Andrew, 2026-08-25).
  const [query, setQuery] = useState(initialQuery ?? '');
  const [preferredType, setPreferredType] = useState<ServiceFeesType>('provider');
  // PROD-2368: a provider-less invoice (device booking) cannot list Provider Fees, so the type is
  // DERIVED: it follows the invoice as it loads, with no effect and no resync.
  const hasProvider = !isEmptyGuid(providerId);
  const type: ServiceFeesType = hasProvider ? preferredType : 'all';

  const feesQuery = useServiceFees(
    { providerId, locationId, patientId, type, query },
    open && hasDiagnosis && !scopeLoading,
  );
  const results = feesQuery.data?.results ?? [];
  const willFetch = canFetchServiceFees({ providerId, patientId, type });
  const status = httpStatusOf(feesQuery.error);

  function pick(serviceId: string) {
    onAdd(serviceId);
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description={hasDiagnosis ? 'Pick a billable service to add to this invoice.' : undefined}
      size="lg"
    >
      {!hasDiagnosis ? (
        <Banner variant="warning" title="Diagnosis needed">
          Please add diagnosis first.
        </Banner>
      ) : (
        <div className="flex flex-col gap-3">
          <Segmented<ServiceFeesType>
            aria-label="Service list"
            variant="pill"
            className="self-start"
            options={[
              { value: 'provider', label: 'Provider Fees', disabled: !hasProvider },
              { value: 'all', label: 'All' },
            ]}
            value={type}
            onChange={setPreferredType}
          />
          {!hasProvider && !scopeLoading && (
            <p className="m-0 text-size-xs text-text-muted">
              This invoice has no provider, so only the full catalogue is available.
            </p>
          )}

          <TextField
            type="text"
            size="compact"
            aria-label="Search services"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or code"
            leadingIcon={<Search className="size-4" aria-hidden />}
          />

          <div className="-mx-1 max-h-80 overflow-y-auto px-1">
            {scopeLoading || feesQuery.isLoading ? (
              <div className="flex flex-col gap-2 py-2" aria-label="Loading services">
                {[0, 1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : feesQuery.isError ? (
              // PROD-2368: a FAILED request is never an empty state.
              <Banner
                variant="error"
                role="alert"
                title="Couldn’t load services"
                action={
                  <Button variant="secondary" size="sm" onClick={() => void feesQuery.refetch()}>
                    Retry
                  </Button>
                }
              >
                {status != null
                  ? `The request was rejected (HTTP ${status}).`
                  : 'The service catalogue could not be reached.'}{' '}
                This is not the same as an empty catalogue.
              </Banner>
            ) : !willFetch ? (
              <p role="status" className="m-0 py-6 text-center text-size-sm text-text-muted">
                This invoice has no patient yet, so its services can&apos;t be listed.
              </p>
            ) : results.length === 0 ? (
              <p className="m-0 py-6 text-center text-size-sm text-text-muted">No services found.</p>
            ) : (
              <ul className="m-0 list-none divide-y divide-border-subtle p-0">
                {results.map((r) => {
                  const id = r.id ?? '';
                  const added = existingServiceIds.includes(id);
                  const fee = feeFor(locationId, r.fees);
                  return (
                    <li key={id}>
                      <button
                        type="button"
                        disabled={added || !id || !!busy}
                        onClick={() => pick(id)}
                        className="flex w-full items-center justify-between gap-4 rounded-md px-2 py-2.5 text-left hover:bg-grey-100 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <span className="min-w-0">
                          <span className="block truncate text-size-sm text-text-primary">
                            {r.procedureCode?.displayName ?? r.shortDescription ?? '—'}
                          </span>
                          <span className="block text-size-xs text-text-muted">
                            {[r.procedureCode?.code, r.procedureCode?.system].filter(Boolean).join(' · ')}
                          </span>
                        </span>
                        <span className="shrink-0 text-size-sm tabular-nums text-text-secondary">
                          {added ? 'Added' : fee != null ? `${currency} ${formatMoney(fee)}` : ''}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
